import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Story } from '../types/Story';
import { Button } from './common/Button';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../constants/colors';
import { useTranslation } from 'react-i18next';
import { getOptimizedImage } from '../utils/cloudinary';

interface StoryReaderProps {
  story: Story;
  onComplete?: () => void;
}

/* ─── Animations ─── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const imageReveal = keyframes`
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
`;

const progressGrow = keyframes`
  from { width: 0%; }
`;

/* ─── Reading Progress ─── */

const ProgressBar = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #e9ecef;
  z-index: 10;
  border-radius: 0 0 3px 3px;
`;

const ProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${props => props.$percent}%;
  background: linear-gradient(90deg, ${COLORS.PRIMARY}, #8b5cf6);
  border-radius: 0 3px 3px 0;
  transition: width 0.15s ease-out;
`;

/* ─── Reader Container ─── */

const ReaderContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

const ReaderCard = styled.article`
  background-color: #fffdf9;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.03);
  border: 1px solid #f0ece4;
  overflow: hidden;
`;

/* ─── Hero Header ─── */

const HeroSection = styled.div<{ $hasImage: boolean }>`
  position: relative;
  text-align: center;
  padding: ${props => props.$hasImage ? '0' : '48px 32px 32px'};
`;

const HeroImage = styled.div<{ $imageUrl: string }>`
  width: 100%;
  height: 440px;
  background-image: url(${props => props.$imageUrl});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 35%;
    background: linear-gradient(to top, #fffdf9 0%, transparent 100%);
  }
  
  @media (max-width: 576px) {
    height: 300px;
  }
`;

const HeroContent = styled.div<{ $overlaid: boolean }>`
  ${props => props.$overlaid && css`
    position: relative;
    margin-top: -80px;
    z-index: 1;
  `}
  padding: 0 32px 32px;
  
  @media (max-width: 576px) {
    padding: 0 20px 24px;
  }
`;

const Title = styled.h1`
  font-size: 1.85rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 12px 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
  
  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 0.825rem;
  color: #868e96;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  
  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
`;

const MetaDivider = styled.span`
  color: #dee2e6;
`;

/* ─── Controls ─── */

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 32px;
  background: #f8f6f0;
  border-top: 1px solid #eee9df;
  border-bottom: 1px solid #eee9df;
  
  @media (max-width: 576px) {
    padding: 10px 20px;
  }
`;

const FontSizeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FontLabel = styled.span`
  font-size: 0.75rem;
  color: #868e96;
  margin-right: 8px;
  font-weight: 500;
`;

const FontSizeButton = styled.button<{ $active: boolean; $size: 'small' | 'medium' | 'large' }>`
  background-color: ${props => props.$active ? COLORS.PRIMARY : 'transparent'};
  color: ${props => props.$active ? 'white' : '#868e96'};
  width: ${props => {
    switch(props.$size) {
      case 'small': return '28px';
      case 'medium': return '32px';
      case 'large': return '36px';
    }
  }};
  height: ${props => {
    switch(props.$size) {
      case 'small': return '28px';
      case 'medium': return '32px';
      case 'large': return '36px';
    }
  }};
  border-radius: 8px;
  border: 1px solid ${props => props.$active ? COLORS.PRIMARY : '#e9ecef'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  font-size: ${props => {
    switch(props.$size) {
      case 'small': return '11px';
      case 'medium': return '14px';
      case 'large': return '17px';
    }
  }};
  
  &:hover {
    background-color: ${props => props.$active ? COLORS.PRIMARY_DARK : '#f1f3f5'};
    border-color: ${props => props.$active ? COLORS.PRIMARY_DARK : '#dee2e6'};
  }
`;

const ReadingTimeEstimate = styled.span`
  font-size: 0.8rem;
  color: #adb5bd;
  display: flex;
  align-items: center;
  gap: 5px;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

/* ─── Story Body ─── */

const StoryBody = styled.div`
  padding: 36px 32px;
  
  @media (max-width: 576px) {
    padding: 24px 20px;
  }
`;

const StorySection = styled.div`
  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const StoryText = styled.div<{ $fontSize: string }>`
  font-size: ${props => props.$fontSize};
  line-height: 1.8;
  color: #2c2c2c;
  
  p {
    margin-bottom: 18px;
    text-align: justify;
    text-justify: inter-word;
    hyphens: auto;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

/* ─── Story Images ─── */

const ImageContainer = styled.figure`
  margin: 28px -8px;
  padding: 0;
  animation: ${imageReveal} 0.5s ease-out;
`;

const StoryImage = styled.img`
  width: 100%;
  border-radius: 12px;
  display: block;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const ImageCaption = styled.figcaption`
  text-align: center;
  font-size: 0.75rem;
  color: #adb5bd;
  margin-top: 8px;
  font-style: italic;
`;

/* ─── Section Divider ─── */

const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 32px 0;
  color: #dee2e6;
  
  &::before, &::after {
    content: '';
    height: 1px;
    flex: 1;
    background: linear-gradient(to right, transparent, #e9ecef, transparent);
  }
  
  svg {
    width: 18px;
    height: 18px;
    color: #ced4da;
    flex-shrink: 0;
  }
`;

/* ─── End of Story ─── */

const StoryEnd = styled.div`
  text-align: center;
  padding: 32px 0 8px;
`;

const EndMark = styled.div`
  font-size: 1.5rem;
  color: #ced4da;
  letter-spacing: 8px;
  margin-bottom: 8px;
`;

const EndText = styled.p`
  font-size: 0.85rem;
  color: #adb5bd;
  margin: 0;
`;

/* ─── Navigation ─── */

const NavigationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 32px;
  background: #f8f6f0;
  border-top: 1px solid #eee9df;
  
  @media (max-width: 576px) {
    padding: 16px 20px;
    flex-direction: column;
    gap: 10px;
    
    & > * {
      width: 100%;
      text-align: center;
    }
  }
`;

/* ─── Enums & Constants ─── */

enum FontSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

const fontSizeValues = {
  [FontSize.SMALL]: '1rem',
  [FontSize.MEDIUM]: '1.125rem',
  [FontSize.LARGE]: '1.3rem',
};

/* ─── Component ─── */

export const StoryReader: React.FC<StoryReaderProps> = ({ story, onComplete }) => {
  const [fontSize, setFontSize] = useState<FontSize>(FontSize.MEDIUM);
  const [readProgress, setReadProgress] = useState<number>(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const readerRef = useRef<HTMLDivElement>(null);

  // Split content into paragraphs
  const paragraphs = story.content.split(/\n+/).filter(p => p.trim() !== '');
  const chunkSize = Math.ceil(paragraphs.length / 2);
  const storyChunks = [
    paragraphs.slice(0, chunkSize),
    paragraphs.slice(chunkSize),
  ];

  // Compute reading time
  const wordCount = story.content.split(/\s+/).length;
  const readingTimeMinutes = Math.max(1, Math.ceil(wordCount / 200));

  // Reading progress tracker
  const handleScroll = useCallback(() => {
    if (!readerRef.current) return;
    
    const el = readerRef.current;
    const rect = el.getBoundingClientRect();
    const totalHeight = el.scrollHeight - window.innerHeight;
    const scrolled = -rect.top;
    const percent = Math.min(100, Math.max(0, (scrolled / totalHeight) * 100));
    setReadProgress(percent);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  const handleBackToLibrary = () => navigate('/library');
  const handleCreateNewStory = () => navigate('/create');

  // Determine hero image (first story image or cover)
  const heroImage = story.imageUrls?.[0] 
    ? getOptimizedImage(story.imageUrls[0], 800) 
    : story.coverImageUrl || null;

  return (
    <ReaderContainer ref={readerRef}>
      <ReaderCard>
        <ProgressBar>
          <ProgressFill $percent={readProgress} />
        </ProgressBar>

        {/* Hero */}
        <HeroSection $hasImage={!!heroImage}>
          {heroImage && <HeroImage $imageUrl={heroImage} />}
          <HeroContent $overlaid={!!heroImage}>
            <Title>{story.title}</Title>
            <MetaInfo>
              <MetaItem>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                </svg>
                {t(`ageRanges.${story.ageRange}`)}
              </MetaItem>
              <MetaDivider>·</MetaDivider>
              <MetaItem>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                </svg>
                {t(`storyTypes.${story.storyType}`)}
              </MetaItem>
              <MetaDivider>·</MetaDivider>
              <MetaItem>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                {story.likes}
              </MetaItem>
            </MetaInfo>
          </HeroContent>
        </HeroSection>

        {/* Controls */}
        <ControlsBar>
          <FontSizeControl>
            <FontLabel>{t('reader.fontSize', { defaultValue: 'Text' })}</FontLabel>
            <FontSizeButton 
              $active={fontSize === FontSize.SMALL} 
              $size="small"
              onClick={() => setFontSize(FontSize.SMALL)}
              aria-label="Small font size"
            >
              A
            </FontSizeButton>
            <FontSizeButton 
              $active={fontSize === FontSize.MEDIUM} 
              $size="medium"
              onClick={() => setFontSize(FontSize.MEDIUM)}
              aria-label="Medium font size"
            >
              A
            </FontSizeButton>
            <FontSizeButton 
              $active={fontSize === FontSize.LARGE} 
              $size="large"
              onClick={() => setFontSize(FontSize.LARGE)}
              aria-label="Large font size"
            >
              A
            </FontSizeButton>
          </FontSizeControl>

          <ReadingTimeEstimate>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            {t('reader.readingTime', { 
              minutes: readingTimeMinutes, 
              defaultValue: `${readingTimeMinutes} min read` 
            })}
          </ReadingTimeEstimate>
        </ControlsBar>

        {/* Story Body */}
        <StoryBody>
          {storyChunks.map((chunk, index) => (
            <StorySection key={index}>
              {/* Image before each chunk (skip first if used as hero) */}
              {story.imageUrls?.[index] && index > 0 && (
                <ImageContainer>
                  <StoryImage
                    src={getOptimizedImage(story.imageUrls[index], 800)}
                    alt={t('reader.illustrationAlt', { 
                      number: index + 1, 
                      defaultValue: `Story illustration ${index + 1}` 
                    })}
                    loading="lazy"
                  />
                </ImageContainer>
              )}

              <StoryText $fontSize={fontSizeValues[fontSize]}>
                {chunk.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </StoryText>

              {index < storyChunks.length - 1 && (
                <SectionDivider>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </SectionDivider>
              )}
            </StorySection>
          ))}

          {/* Final image */}
          {story.imageUrls?.[2] && (
            <ImageContainer>
              <StoryImage
                src={getOptimizedImage(story.imageUrls[2], 800)}
                alt={t('reader.illustrationAlt', { 
                  number: 3, 
                  defaultValue: 'Story illustration 3' 
                })}
                loading="lazy"
              />
            </ImageContainer>
          )}

          {/* End mark */}
          <StoryEnd>
            <EndMark>~ ~ ~</EndMark>
            <EndText>{t('reader.endOfStory', { defaultValue: 'The End' })}</EndText>
          </StoryEnd>
        </StoryBody>

        {/* Navigation */}
        <NavigationBar>
          <Button
            variant="outline"
            onClick={handleBackToLibrary}
            style={{
              borderColor: COLORS.PRIMARY,
              color: COLORS.PRIMARY
            }}
          >
            {t('reader.backToLibrary', { defaultValue: 'Back to Library' })}
          </Button>

          <Button
            variant="primary"
            onClick={handleCreateNewStory}
          >
            {t('reader.createNewStory', { defaultValue: 'Create New Story' })}
          </Button>
        </NavigationBar>
      </ReaderCard>
    </ReaderContainer>
  );
};